import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Trash2, GripVertical, MessageSquare, Clock, Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function FollowUpSequenceSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    is_active: true,
    auto_start: true,
    steps: []
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/settings/follow-up-sequence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate('/settings');
      } else {
        toast.error("Failed to load settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/settings/follow-up-sequence`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Follow-up sequence saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    const newStep = {
      step_id: `step_${Date.now()}`,
      delay_value: 1,
      delay_unit: "days",
      message_template: "Hi {{name}}, just checking in about your screen estimate. Book here: {{booking_link}}"
    };
    setConfig({ ...config, steps: [...config.steps, newStep] });
  };

  const removeStep = (index) => {
    const newSteps = config.steps.filter((_, i) => i !== index);
    setConfig({ ...config, steps: newSteps });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...config.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setConfig({ ...config, steps: newSteps });
  };

  const formatDelay = (step) => {
    const unit = step.delay_unit === "minutes" ? "min" : 
                 step.delay_unit === "hours" ? "hr" : "day";
    return `${step.delay_value} ${unit}${step.delay_value !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-zinc-100 rounded-lg"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-barlow text-xl font-bold text-zinc-900">Follow-up Sequence</h1>
            <p className="text-sm text-zinc-500">Automated messages for new estimates</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#207024] hover:bg-[#1a5c1d] text-white rounded-lg font-medium disabled:opacity-50"
            data-testid="save-button"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 pb-24 space-y-6">
        {/* Global Settings */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-4">
          <h2 className="font-semibold text-zinc-900">Settings</h2>
          
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Sequence Active</Label>
              <p className="text-sm text-zinc-500">Enable or disable all automated follow-ups</p>
            </div>
            <Switch
              checked={config.is_active}
              onCheckedChange={(checked) => setConfig({ ...config, is_active: checked })}
              data-testid="toggle-active"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Auto-Start for New Estimates</Label>
              <p className="text-sm text-zinc-500">Automatically start sequence when estimate is created</p>
            </div>
            <Switch
              checked={config.auto_start}
              onCheckedChange={(checked) => setConfig({ ...config, auto_start: checked })}
              data-testid="toggle-auto-start"
            />
          </div>
        </div>

        {/* Variables Reference */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-semibold text-amber-900 mb-2">Available Variables</h3>
          <div className="text-sm text-amber-800 space-y-1">
            <p><code className="bg-amber-100 px-1 rounded">{"{{name}}"}</code> - Customer's first name</p>
            <p><code className="bg-amber-100 px-1 rounded">{"{{booking_link}}"}</code> - Link to resume/book estimate</p>
            <p><code className="bg-amber-100 px-1 rounded">{"{{estimate_total}}"}</code> - Quoted price (e.g., $350.00)</p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-zinc-900">Follow-up Steps</h2>
            <button
              onClick={addStep}
              className="flex items-center gap-2 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-sm font-medium"
              data-testid="add-step-button"
            >
              <Plus className="w-4 h-4" />
              Add Step
            </button>
          </div>

          {config.steps.length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-200 border-dashed p-8 text-center">
              <MessageSquare className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500 mb-4">No follow-up steps configured</p>
              <button
                onClick={addStep}
                className="px-4 py-2 bg-[#207024] hover:bg-[#1a5c1d] text-white rounded-lg font-medium"
              >
                Add First Step
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {config.steps.map((step, index) => (
                <div
                  key={step.step_id}
                  className="bg-white rounded-xl border border-zinc-200 p-4"
                  data-testid={`step-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-2 text-zinc-400 pt-1">
                      <GripVertical className="w-4 h-4" />
                      <span className="text-sm font-medium text-zinc-500">#{index + 1}</span>
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      {/* Delay */}
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-zinc-400" />
                        <span className="text-sm text-zinc-600">Send after</span>
                        <Input
                          type="number"
                          min="1"
                          value={step.delay_value}
                          onChange={(e) => updateStep(index, 'delay_value', parseInt(e.target.value) || 1)}
                          className="w-20 h-8"
                          data-testid={`step-${index}-delay-value`}
                        />
                        <select
                          value={step.delay_unit}
                          onChange={(e) => updateStep(index, 'delay_unit', e.target.value)}
                          className="h-8 rounded-lg border border-zinc-200 text-sm px-2"
                          data-testid={`step-${index}-delay-unit`}
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                        {index > 0 && (
                          <span className="text-xs text-zinc-400">after previous step</span>
                        )}
                      </div>

                      {/* Message */}
                      <Textarea
                        value={step.message_template}
                        onChange={(e) => updateStep(index, 'message_template', e.target.value)}
                        placeholder="Enter your follow-up message..."
                        rows={3}
                        className="text-sm"
                        data-testid={`step-${index}-message`}
                      />
                      
                      {/* Character count */}
                      <div className="flex justify-between text-xs text-zinc-400">
                        <span>{step.message_template.length} characters</span>
                        {step.message_template.length > 160 && (
                          <span className="text-amber-500">May be split into multiple SMS</span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => removeStep(index)}
                      className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                      data-testid={`step-${index}-delete`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        {config.steps.length > 0 && (
          <div className="bg-zinc-100 rounded-xl p-4">
            <h3 className="font-semibold text-zinc-700 mb-3">Sequence Preview</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-zinc-600">Estimate created</span>
              </div>
              {config.steps.map((step, index) => (
                <div key={step.step_id} className="flex items-center gap-2 text-sm pl-1">
                  <div className="w-px h-4 bg-zinc-300 ml-1"></div>
                  <span className="text-zinc-500">{formatDelay(step)} later →</span>
                  <span className="text-zinc-700">SMS #{index + 1}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm text-zinc-400 pl-1">
                <div className="w-px h-4 bg-zinc-300 ml-1"></div>
                <span>Pauses if customer responds</span>
              </div>
            </div>
          </div>
        )}

        {/* Note */}
        <div className="text-sm text-zinc-500 text-center">
          <p>Sequences automatically pause when a customer responds.</p>
          <p>Each follow-up counts toward the estimate's follow-up counter.</p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
